import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { useRoute } from 'vue-router'
import { storeToRefs } from 'pinia'
import { Chart } from 'chart.js'
import type { TooltipItem } from 'chart.js'
import { useResultStore } from '@/stores/result'
import { useCalculationStore } from '@/stores/calculation'
import { useChartConfigurationStore } from '@/stores/chartConfiguration'
import { DEFAULT_CHART_CONFIGURATION } from '@/stores/chartConfiguration'
import type { DatasetType } from '@/views/calculations/type/resultView.type'
import type { Result } from '@/stores/type/result.type'
import type { ChartConfiguration } from '@/stores/type/chartConfiguration.type'
import type { PivotFormData } from '@/components/calculation-result/PivotChartDialog.vue'
import { assembleChartData } from '@/views/calculations/utils/resultChartUtils'
import { formatMoney } from '@/views/calculations/utils/resultChart/format'
import {
  buildMaterialCategories,
  buildTrajectLabels,
  dimensionValues
} from '@/views/calculations/utils/resultChart/dimensions'
import { buildTooltipLines, sumSelectedPhases } from '@/views/calculations/utils/resultChart/phases'
import {
  computeMaxColumnValue,
  computeMaxTotal
} from '@/views/calculations/utils/resultChart/metrics'
import { ensureResultChartRegistered } from '@/views/calculations/utils/resultChart/registration'
import {
  applyRowOverrides,
  aggregateResultsForTooltip
} from '@/views/calculations/utils/resultChart/results'
import {
  buildResultChartScales,
  buildResultChartDataLabels
} from '@/views/calculations/utils/resultChartOptions'
import {
  CHART_AXIS_GROUPINGS,
  CHART_DISPLAY_MODES,
  STACK_DIMENSION_BY_DISPLAY_MODE,
  DEFAULT_CHART_AXIS_GROUPING,
  DEFAULT_DISPLAY_MODE,
  DEFAULT_CHART_Y_MEASURE
} from '@/constants/chartConfiguration'
import { useCalculationNavigation } from '@/views/calculations/composables/calculationResult/useCalculationNavigation'
import { useChartControls } from '@/views/calculations/composables/calculationResult/useChartControls'
import { useChartExportActions } from '@/views/calculations/composables/calculationResult/useChartExportActions'
import { EDIT_SCOPES, type EditScope } from '@/constants/editScope'

export const useCalculationResultView = () => {
  const route = useRoute()

  const calculationId = computed(() => Number(route.params.calculationId))
  const projectId = computed(() => Number(route.params.id))

  const calculationStore = useCalculationStore()
  const resultStore = useResultStore()
  const chartConfigurationStore = useChartConfigurationStore()
  const { items: resultItems, fetchLoading: resultFetchLoading } = storeToRefs(resultStore)
  const { items: chartConfigItems } = storeToRefs(chartConfigurationStore)

  const canvasRef = ref<HTMLCanvasElement | null>(null)

  const showListView = ref(false)
  const activeConfigId = ref<number | null>(null)
  const activeConfig = computed<ChartConfiguration | null>(
    () =>
      chartConfigItems.value.find(
        (chartConfiguration) => chartConfiguration.id === activeConfigId.value
      ) ?? null
  )
  const { currentItem: currentConfig } = storeToRefs(chartConfigurationStore)

  const pivotDialogOpen = ref(false)
  const pivotDialogConfig = ref<ChartConfiguration | null>(null)

  const editSourceRowsOpen = ref(false)

  const openEditSourceRows = () => {
    editSourceRowsOpen.value = true
  }

  const onSourceRowsSaved = async (payload?: {
    renames?: { from: string; to: string }[]
    scope?: EditScope
  }) => {
    const renames = payload?.renames ?? []
    const map = new Map(renames.map((rename) => [rename.from, rename.to]))
    const remap = (values: string[]) => [...new Set(values.map((value) => map.get(value) ?? value))]

    if (renames.length) {
      activeFilters.value = remap(activeFilters.value)
      activeGroupFilters.value = remap(activeGroupFilters.value)
    }
    for (const config of chartConfigItems.value) {
      const nextFilter = renames.length
        ? remap(config.filter_material_categories ?? [])
        : (config.filter_material_categories ?? [])
      const clearOverrides =
        payload?.scope === EDIT_SCOPES.ALL && (config.row_overrides?.length ?? 0) > 0
      const previousFilter = config.filter_material_categories ?? []
      const filterChanged =
        nextFilter.length !== previousFilter.length ||
        nextFilter.some((value, index) => value !== previousFilter[index])
      if (clearOverrides || filterChanged) {
        await chartConfigurationStore.updateConfiguration({
          ...config,
          filter_material_categories: nextFilter,
          ...(clearOverrides ? { row_overrides: [] } : {})
        })
      }
    }
    const activeConfig = chartConfigItems.value.find((config) => config.id === activeConfigId.value)
    if (activeConfig) chartConfigurationStore.setCurrentItem(activeConfig)

    await resultStore.fetchItems({ calculation: calculationId.value })
  }

  const activeFilters = ref<string[]>([])
  const activeGroupFilters = ref<string[]>([])
  const maxTotal = ref(0)

  let chart: Chart<'bar'> | null = null

  const isDatasetType = (dataset: unknown): dataset is DatasetType => {
    return (
      typeof dataset === 'object' &&
      dataset !== null &&
      'label' in dataset &&
      'data' in dataset &&
      Array.isArray(dataset.data)
    )
  }

  const getChartDatasets = () => chart?.data.datasets.filter(isDatasetType) ?? []

  const isTotalDataset = (dataset: object | undefined) => {
    if (!dataset || !('isTotal' in dataset)) return false
    return dataset.isTotal === true
  }

  const chartConfiguration = computed<ChartConfiguration | null>(() => currentConfig.value)

  const { navigateToInput, navigateBackToProject, confirmReset } = useCalculationNavigation({
    projectId,
    calculationId
  })

  const {
    selectedPhases,
    phaseFilterOptions,
    isPhaseActive,
    togglePhase,
    sortOrder,
    sortOrderOptions,
    setSortOrder
  } = useChartControls(chartConfiguration)

  const {
    exportDialog,
    exportDialogConfig,
    exportFileTypes,
    openChartExportDialog,
    downloadChartFromDialog,
    downloadResultsExcel
  } = useChartExportActions({
    resultItems,
    calculationId,
    activeConfigId,
    activeFilters,
    activeGroupFilters
  })

  const effectiveResults = computed<Result[]>(() =>
    applyRowOverrides(resultItems.value, chartConfiguration.value)
  )

  const trajectLabels = computed(() =>
    buildTrajectLabels(
      effectiveResults.value,
      chartConfiguration.value?.x_axis_grouping ?? DEFAULT_CHART_AXIS_GROUPING
    )
  )
  const materialCategories = computed(() => buildMaterialCategories(effectiveResults.value))
  const filterItems = computed(() => materialCategories.value)
  const seedCategoryFilters = (selected: string[]) => {
    activeFilters.value =
      selected.length > 0
        ? selected.filter((category) => materialCategories.value.includes(category))
        : [...materialCategories.value]
  }

  const displayMode = computed(() => chartConfiguration.value?.display_mode ?? DEFAULT_DISPLAY_MODE)
  const isPhaseMode = computed(
    () =>
      displayMode.value === CHART_DISPLAY_MODES.PHASE_STACK ||
      displayMode.value === CHART_DISPLAY_MODES.PHASE_GROUPED
  )

  const stackDimension = computed(
    () => STACK_DIMENSION_BY_DISPLAY_MODE[displayMode.value] ?? CHART_AXIS_GROUPINGS.MATERIAL
  )

  const totalMki = computed<number>(() => {
    const grouping = chartConfiguration.value?.x_axis_grouping ?? DEFAULT_CHART_AXIS_GROUPING
    const materials = activeFilters.value
    const groups = activeGroupFilters.value

    const isVisible = (result: Result): boolean =>
      dimensionValues(result, grouping).some((value) => groups.includes(value))

    let sum = 0
    for (const result of effectiveResults.value) {
      if (materials.length && !materials.includes(result.material_display_name)) continue
      if (!isVisible(result)) continue
      sum += sumSelectedPhases(result, selectedPhases.value)
    }

    return sum
  })

  const totalMkiLabel = computed(() => formatMoney(totalMki.value))

  const isLoading = computed(() => resultFetchLoading.value)

  const updateChart = () => {
    if (!chart) return

    const config = chartConfiguration.value
    if (!config) return

    const { labels, datasets } = assembleChartData({
      results: resultItems.value,
      config,
      categoryFilters: activeFilters.value,
      groupFilters: activeGroupFilters.value
    })

    maxTotal.value =
      displayMode.value === CHART_DISPLAY_MODES.PHASE_GROUPED
        ? computeMaxColumnValue(datasets, labels.length)
        : computeMaxTotal(datasets, labels.length)

    chart.data.labels = labels
    chart.data.datasets = datasets
    if (chart.options.scales?.x?.title) {
      chart.options.scales.x.title.text = config.x_axis_title ?? 'Location'
    }
    if (chart.options.scales?.y?.title) {
      chart.options.scales.y.title.text = config.y_axis_title ?? 'MKI [euro]'
    }
    chart.update()
  }

  const toggleFilter = (filterName: string) => {
    const index = activeFilters.value.indexOf(filterName)
    if (index > -1) {
      activeFilters.value.splice(index, 1)
    } else {
      activeFilters.value.push(filterName)
    }
    updateChart()
  }

  const isFilterActive = (filterName: string) => activeFilters.value.includes(filterName)

  const groupFilterLabel = computed(() => {
    switch (chartConfiguration.value?.x_axis_grouping) {
      case CHART_AXIS_GROUPINGS.MATERIAL:
        return 'Material'
      case CHART_AXIS_GROUPINGS.BUILDING_BLOCK:
        return 'Building Block'
      case CHART_AXIS_GROUPINGS.LABEL:
        return 'Label'
      default:
        return 'Location'
    }
  })

  const toggleGroupFilter = (group: string) => {
    const index = activeGroupFilters.value.indexOf(group)
    if (index > -1) {
      activeGroupFilters.value.splice(index, 1)
    } else {
      activeGroupFilters.value.push(group)
    }
    updateChart()
  }

  const isGroupFilterActive = (group: string) => activeGroupFilters.value.includes(group)

  const openChart = async (config: ChartConfiguration) => {
    activeConfigId.value = config.id
    showListView.value = false
    chartConfigurationStore.setCurrentItem(config)
    seedCategoryFilters(config.filter_material_categories ?? [])
    await nextTick()
    activeGroupFilters.value = [...trajectLabels.value]
    updateChart()
  }

  const backToList = () => {
    showListView.value = true
  }

  const openCreateChartDialog = () => {
    pivotDialogConfig.value = null
    pivotDialogOpen.value = true
  }

  const openEditChartDialog = () => {
    pivotDialogConfig.value = chartConfiguration.value
    pivotDialogOpen.value = true
  }

  const savePivotDialog = async (form: PivotFormData) => {
    const {
      name,
      x_axis_title,
      y_axis_title,
      x_axis_grouping,
      selected_phases,
      display_mode,
      sort_order,
      filter_material_categories
    } = form

    if (pivotDialogConfig.value) {
      await chartConfigurationStore.updateConfiguration({
        ...pivotDialogConfig.value,
        name,
        x_axis_title,
        y_axis_title,
        x_axis_grouping,
        selected_phases,
        display_mode,
        sort_order,
        filter_material_categories,
        row_overrides: []
      })
    } else {
      const created = await chartConfigurationStore.create({
        calculation: calculationId.value,
        name,
        order: chartConfigItems.value.length,
        y_axis_measure: DEFAULT_CHART_Y_MEASURE,
        x_axis_title,
        y_axis_title,
        x_axis_grouping,
        selected_phases,
        display_mode,
        sort_order,
        filter_material_categories
      })
      if (created && !Array.isArray(created)) {
        await chartConfigurationStore.fetchByCalculation(calculationId.value)
        await openChart(created)
      }
    }
    seedCategoryFilters(filter_material_categories)
    updateChart()
  }

  const deleteChart = async (config: ChartConfiguration) => {
    if (chartConfigItems.value.length <= 1) return
    await chartConfigurationStore.delete(config)
    await chartConfigurationStore.fetchByCalculation(calculationId.value)
    if (activeConfigId.value === config.id) {
      const remaining = chartConfigItems.value
      if (remaining.length === 1) {
        await openChart(remaining[0])
      } else {
        showListView.value = true
      }
    }
  }

  const getResultsByContext = (context: TooltipItem<'bar'>): Result[] => {
    // dataset.label is the stack-dimension value; context.label is the x-dimension value.
    const stackKey = String(context.dataset.label ?? '')
    const trajectory = String(context.label ?? '')
    const grouping = chartConfiguration.value?.x_axis_grouping ?? DEFAULT_CHART_AXIS_GROUPING
    const stackDim = stackDimension.value
    return effectiveResults.value.filter(
      (result) =>
        dimensionValues(result, stackDim).includes(stackKey) &&
        dimensionValues(result, grouping).includes(trajectory)
    )
  }

  onMounted(async () => {
    await Promise.all([
      resultStore.fetchItems({ calculation: calculationId.value }),
      chartConfigurationStore.fetchByCalculation(calculationId.value),
      calculationStore.currentItem?.id !== calculationId.value
        ? calculationStore.fetchItem(calculationId.value)
        : Promise.resolve()
    ])

    const configList = chartConfigItems.value
    if (configList.length === 0) {
      const calcName = calculationStore.currentItem?.name ?? 'Chart'
      const created = await chartConfigurationStore.create({
        calculation: calculationId.value,
        ...DEFAULT_CHART_CONFIGURATION,
        name: calcName
      })
      if (created && !Array.isArray(created)) {
        await chartConfigurationStore.fetchByCalculation(calculationId.value)
        activeConfigId.value = created.id
        chartConfigurationStore.setCurrentItem(created)
      }
      showListView.value = false
    } else if (configList.length === 1) {
      activeConfigId.value = configList[0].id
      chartConfigurationStore.setCurrentItem(configList[0])
      showListView.value = false
    } else {
      showListView.value = true
    }

    seedCategoryFilters(chartConfiguration.value?.filter_material_categories ?? [])
    activeGroupFilters.value = [...trajectLabels.value]
  })

  const initChart = (canvas: HTMLCanvasElement) => {
    if (chart) {
      chart.destroy()
      chart = null
    }
    ensureResultChartRegistered()
    chart = new Chart(canvas, {
      type: 'bar',
      data: {
        labels: trajectLabels.value,
        datasets: []
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        layout: {
          padding: { top: 60 }
        },
        plugins: {
          legend: {
            position: 'bottom'
          },
          tooltip: {
            filter: (item) => {
              const dataset = item.chart.data.datasets[item.datasetIndex]
              return !isTotalDataset(dataset)
            },
            callbacks: {
              label: (context: TooltipItem<'bar'>) => {
                const value = context.parsed.y ?? 0
                return `${context.dataset.label}: ${formatMoney(value)}`
              },
              afterLabel: (context: TooltipItem<'bar'>) => {
                if (isPhaseMode.value) return []
                return buildTooltipLines(aggregateResultsForTooltip(getResultsByContext(context)))
              }
            }
          },
          datalabels: buildResultChartDataLabels({
            getDatasets: () => getChartDatasets(),
            displayMode: () => displayMode.value,
            threshold: () => maxTotal.value // live keeps the 5%-of-max hide
          })
        },
        scales: buildResultChartScales({
          x_axis_title: chartConfiguration.value?.x_axis_title ?? 'Location',
          y_axis_title: chartConfiguration.value?.y_axis_title ?? 'MKI [euro]'
        })
      }
    })
    updateChart()
  }

  watch(canvasRef, async (canvas) => {
    if (canvas) {
      await nextTick()
      if (canvasRef.value) initChart(canvasRef.value)
    } else {
      chart?.destroy()
      chart = null
    }
  })

  watch(trajectLabels, (labels) => {
    activeGroupFilters.value = [...labels]
  })

  watch(effectiveResults, () => updateChart())

  watch(
    chartConfiguration,
    () => {
      updateChart()
    },
    { deep: true }
  )

  onBeforeUnmount(() => {
    chart?.destroy()
    chart = null
  })

  return {
    calculationStore,
    calculationId,
    chartConfiguration,
    canvasRef,
    exportDialog,
    exportDialogConfig,
    exportFileTypes,
    filterItems,
    isFilterActive,
    toggleFilter,
    trajectLabels,
    groupFilterLabel,
    activeGroupFilters,
    isGroupFilterActive,
    toggleGroupFilter,
    phaseFilterOptions,
    isPhaseActive,
    togglePhase,
    sortOrder,
    sortOrderOptions,
    setSortOrder,
    confirmReset,
    navigateToInput,
    navigateBackToProject,
    isLoading,
    chartConfigItems,
    showListView,
    activeConfig,
    openChart,
    backToList,
    editSourceRowsOpen,
    openEditSourceRows,
    onSourceRowsSaved,
    pivotDialogOpen,
    pivotDialogConfig,
    openCreateChartDialog,
    openEditChartDialog,
    savePivotDialog,
    deleteChart,
    openChartExportDialog,
    downloadChartFromDialog,
    downloadResultsExcel,
    materialCategories,
    totalMkiLabel
  }
}
